import React, { useState, useEffect, useMemo, Suspense, lazy } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import api from "../lib/apiClient";
import {
  MapPin, Eye, X, MessageCircle, Send, Sparkles, Share2, Check
} from "lucide-react";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { ScrollArea } from "../components/ui/scroll-area";
import { useAuth } from "../context/AuthContext";
import { ClapIcon } from "../lib/icons";
import ShareButton from "../components/shared/ShareButton";
import MakeOfferModal from "../components/marketplace/MakeOfferModal";

// 2D Periscope-style political map (Carto dark_all tiles + emerald
// pins). Replaces the prior 3D satellite globe — flat outlines with
// country/state/city labels are dramatically more useful than a fuzzy
// space photo at any zoom level. Lazy-loaded so Leaflet only ships
// when the user actually visits /map.
const WorldMapView = lazy(() => import("../components/map/WorldMapView"));

// (Leaflet helper components removed — Globe is now the only view.)

const MapPage = () => {
  const [videos, setVideos] = useState([]);
  const [openCommissions, setOpenCommissions] = useState([]);
  const [leaderboard, setLeaderboard] = useState([]);
  const [mapMode, setMapMode] = useState('both'); // 'videos' | 'offers' | 'both'
  const [expandedVideo, setExpandedVideo] = useState(null);
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState("");
  const [loadingComments, setLoadingComments] = useState(false);
  const [submittingComment, setSubmittingComment] = useState(false);
  const [followingSet, setFollowingSet] = useState(new Set());
  // eslint-disable-next-line no-unused-vars
  const [followBusy, setFollowBusy] = useState(null);
  const [accepting, setAccepting] = useState(null);
  const [expandedCommission, setExpandedCommission] = useState(null);
  const [offeringVideo, setOfferingVideo] = useState(null);
  // Current map view as published by WorldMapView via `map-viewchange`.
  // Used to compose a deep-link URL on demand for the Share-view pill.
  const [currentView, setCurrentView] = useState(null);
  const [shareCopied, setShareCopied] = useState(false);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, token } = useAuth();

  // Deep-link initial view from query params: /map?lat=48.85&lng=2.29&zoom=10.
  // Lets share URLs like "what's happening in Paris right now" land the
  // user directly on the city instead of the default zoomed-out world.
  // Parsed once, passed to WorldMapView — subsequent prop updates are
  // ignored by the map (initial-only contract on the receiving side).
  const initialView = useMemo(() => {
    const lat = parseFloat(searchParams.get("lat"));
    const lng = parseFloat(searchParams.get("lng"));
    const zoom = parseInt(searchParams.get("zoom"), 10);
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
    if (lat < -90 || lat > 90 || lng < -180 || lng > 180) return null;
    return {
      lat,
      lng,
      zoom: Number.isFinite(zoom) ? Math.min(18, Math.max(2, zoom)) : 8,
    };
  }, [searchParams]);

  useEffect(() => {
    const fetchVideos = async () => {
      try {
        const res = await api.get(`/videos/map`);
        setVideos(res.data);
      } catch (err) { console.error(err); }
    };
    const fetchCommissions = async () => {
      try {
        const res = await api.get(`/commissions/open`);
        // Hide TEST_* artifacts from the pytest suite — those should
        // never show up as orange pins on the public map.
        const cleaned = (res.data || []).filter(
          (c) => !(c.title || "").startsWith("TEST_")
        );
        setOpenCommissions(cleaned);
      } catch (err) { console.error(err); }
    };
    const fetchLeaderboard = async () => {
      try {
        const res = await api.get(`/commissions/leaderboard?period_days=1&limit=5`);
        setLeaderboard(res.data?.commissions || []);
      } catch (err) { /* silent — optional widget */ }
    };
    fetchVideos();
    fetchCommissions();
    fetchLeaderboard();
  }, []);

  // Listen to the current view broadcast by WorldMapView so the
  // Share-view pill always has fresh coords to bake into the URL.
  useEffect(() => {
    const onView = (e) => setCurrentView(e.detail || null);
    window.addEventListener("map-viewchange", onView);
    return () => window.removeEventListener("map-viewchange", onView);
  }, []);

  const handleShareView = async () => {
    if (!currentView) return;
    const { lat, lng, zoom } = currentView;
    // 4 decimals = ~11 m precision — plenty for "share this view" and
    // keeps the URL human-readable for paste-into-tweet workflows.
    const url = `${window.location.origin}/map?lat=${lat.toFixed(4)}&lng=${lng.toFixed(4)}&zoom=${zoom}`;
    try {
      await navigator.clipboard.writeText(url);
      setShareCopied(true);
      toast.success("Map link copied — paste it anywhere");
      setTimeout(() => setShareCopied(false), 2000);
    } catch {
      // Fallback: open the URL in a new tab so the user can copy from
      // the address bar. Older iOS WKWebView blocks clipboard from
      // background contexts; this still lets them grab it.
      window.prompt("Copy this map link:", url);
    }
  };

  const handleAcceptCommission = async (commissionId) => {
    if (!user) { toast.error("Sign in to accept a commission"); return; }
    setAccepting(commissionId);
    try {
      await api.post(`/commissions/${commissionId}/lock`, {});
      navigate(`/dashboard?commission=${commissionId}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't accept commission");
    }
    setAccepting(null);
  };

  // Pre-load the set of usernames the current user already follows so popups
  // can show Follow / Following state correctly.
  useEffect(() => {
    const fetchFollowing = async () => {
      if (!user) { setFollowingSet(new Set()); return; }
      try {
        const res = await api.get(`/users/${user.username}/following`);
        setFollowingSet(new Set(res.data.map((u) => u.username)));
      } catch (err) {
        console.error("Failed to load following list", err);
      }
    };
    fetchFollowing();
  }, [user]);

  const handleToggleFollow = async (e, targetUsername) => {
    e.stopPropagation();
    if (!user) { toast.error("Sign in to follow creators"); return; }
    if (!targetUsername || targetUsername === user.username) return;
    const isFollowing = followingSet.has(targetUsername);
    setFollowBusy(targetUsername);
    try {
      if (isFollowing) {
        await api.delete(`/users/${targetUsername}/follow`);
        setFollowingSet((prev) => { const n = new Set(prev); n.delete(targetUsername); return n; });
        toast.success(`Unfollowed @${targetUsername}`);
      } else {
        await api.post(`/users/${targetUsername}/follow`, {});
        setFollowingSet((prev) => new Set(prev).add(targetUsername));
        toast.success(`Now following @${targetUsername}`);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Action failed");
    }
    setFollowBusy(null);
  };

  // Fetch comments when expanded video changes
  useEffect(() => {
    const fetchComments = async () => {
      if (expandedVideo) {
        setLoadingComments(true);
        try {
          const res = await api.get(`/videos/${expandedVideo.id}/comments`);
          setComments(res.data);
        } catch (err) {
          console.error(err);
          setComments([]);
        }
        setLoadingComments(false);
      } else {
        setComments([]);
        setNewComment("");
      }
    };
    fetchComments();
  }, [expandedVideo]);

  const handleSubmitComment = async (e) => {
    e.preventDefault();
    if (!user || !newComment.trim() || !expandedVideo) return;
    setSubmittingComment(true);
    try {
      const res = await api.post(`/videos/${expandedVideo.id}/comments`,
        { content: newComment }
      );
      setComments([res.data.comment || res.data, ...comments]);
      setNewComment("");
      toast.success("Comment added!");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to add comment");
    }
    setSubmittingComment(false);
  };

  return (
    <div
      className="relative bg-[#e9e5dc] overscroll-none"
      style={{
        // Fill viewport MINUS the header (3.5rem mobile / 4rem desktop)
        // PLUS the iOS notch — matches the body's padding-top so the
        // globe fills the visible area exactly with no overflow. dvh
        // (not vh) tracks Safari's collapsing toolbar so the header
        // never shifts up/down while panning the map.
        height: 'calc(100dvh - 3.5rem - env(safe-area-inset-top, 0px))',
        overflow: 'hidden',
      }}
      data-testid="map-page"
    >
      <Suspense fallback={<div className="h-full w-full bg-[#e9e5dc] flex items-center justify-center text-emerald-700 text-sm">Loading map…</div>}>
        <WorldMapView
          videos={(mapMode !== 'offers') ? videos : []}
          commissions={(mapMode !== 'videos') ? openCommissions : []}
          initialView={initialView}
          onPointClick={(item, kind) => {
            if (kind === 'video') {
              setExpandedVideo(item);
            } else if (kind === 'commission') {
              // Always show the peek modal so guests can see what the
              // pin is BEFORE deciding to sign up. Logged-in users get
              // the same modal with a real Accept button.
              setExpandedCommission(item);
            }
          }}
        />
      </Suspense>

      {/* Top-boosted leaderboard — top-right widget. Hovering a row pans the
          map to that commission so the user can spot it instantly.
          Iter-58 (May 31): z-index was z-[1000], which is ONE tier higher
          than the header's z-index of 999. On phones the leaderboard
          overlaps the hamburger button's hit area and the leaderboard
          intercepted the tap that should have hit the hamburger,
          freezing the menu inside /map. Two-part fix:
            • z-[998] — strictly below the header, so the header always
              wins any pointer conflict.
            • top-14 sm:top-16 on mobile — pushes the leaderboard BELOW
              the header band (3.5rem / 4rem high) so it can never sit
              in the same row as the hamburger to begin with. */}
      {leaderboard.length > 0 && (
        <div className="absolute top-14 sm:top-4 right-2 sm:right-4 z-[998] bg-gray-900/90 border border-cyan-500/30 rounded-lg p-2 sm:p-3 max-w-[240px] backdrop-blur-sm shadow-xl" data-testid="map-leaderboard">
          <h4 className="text-cyan-300 text-[11px] uppercase tracking-wider font-bold flex items-center gap-1 mb-1.5">
            ⚡ Top boosted today
          </h4>
          <ol className="space-y-1">
            {leaderboard.map((c, idx) => (
              <li
                key={c.id}
                className="text-[11px] text-gray-300 flex items-start gap-2 hover:bg-cyan-500/10 hover:border-cyan-500/40 border border-transparent rounded px-1 py-0.5 cursor-pointer transition-colors"
                onMouseEnter={() => {
                  if (c.location?.lat != null && c.location?.lng != null) {
                    window.dispatchEvent(new CustomEvent('map-spotlight', { detail: { lat: c.location.lat, lng: c.location.lng } }));
                  }
                }}
                onClick={() => navigate(`/commissions`)}
                data-testid={`map-leaderboard-${idx}`}
                title="Hover to peek on map · click to open in Commissions hub"
              >
                <span className="text-cyan-400 font-bold w-3 shrink-0">{idx + 1}</span>
                <div className="flex-1 min-w-0">
                  <div className="truncate text-white font-medium">{c.title}</div>
                  <div className="text-gray-500 text-[10px] truncate">
                    {c.ec_anonymous || !c.ec_username ? <em>anon</em> : `@${c.ec_username}`}
                    {' · '}<span className="text-cyan-300">×{c.boost_count} boosts</span>
                  </div>
                </div>
              </li>
            ))}
          </ol>
        </div>
      )}

      {/* Compact filter chip — shows current mode + cycles on tap.
          Tiny footprint so the globe stays the hero on mobile. */}
      <div
        className="absolute left-2 sm:left-4 z-[1000] flex items-center gap-1.5 flex-wrap"
        style={{
          // Lifted above the Emergent platform badge (~3rem tall) +
          // standard safe-area + breathing room so the Both/Videos/Offers
          // pills never crowd the bottom-left logo. Right margin reserves
          // the record-FAB column so pills can never sit under it.
          bottom: 'calc(env(safe-area-inset-bottom, 0px) + 5.5rem)',
          maxWidth: 'calc(100% - 6.5rem)',
        }}
      >
        <div className="inline-flex items-center bg-gray-900/90 border border-gray-800 rounded-full p-0.5 shadow-xl backdrop-blur-sm" data-testid="map-mode-pill">
          {[
            { key: 'both', label: 'Both' },
            { key: 'videos', label: 'Videos' },
            { key: 'offers', label: 'Offers' },
          ].map((t) => (
            <button
              key={t.key}
              onClick={() => setMapMode(t.key)}
              className={`text-[11px] font-medium px-2.5 py-1 rounded-full transition-colors ${
                mapMode === t.key
                  ? (t.key === 'offers' ? 'bg-amber-500 text-gray-900' : 'bg-emerald-600 text-white')
                  : 'text-gray-400 hover:text-white'
              }`}
              data-testid={`map-mode-${t.key}`}
              aria-pressed={mapMode === t.key}
            >
              {t.label}
            </button>
          ))}
        </div>
        <span className="text-gray-400 text-[10px] tabular-nums hidden sm:inline">
          {mapMode === 'offers' ? `${openCommissions.length} offers` :
           mapMode === 'videos' ? `${videos.length} videos` :
           `${videos.length}v · ${openCommissions.length}o`}
        </span>
        {/* Share-this-view pill — copies a /map?lat=&lng=&zoom= URL
            pointing at the user's current pan/zoom. Self-disables until
            the map has fired its first moveend so we never publish a
            stale 0,0 link. */}
        <button
          type="button"
          onClick={handleShareView}
          disabled={!currentView}
          className="inline-flex items-center gap-1 text-[11px] font-medium px-2.5 py-1 rounded-full bg-gray-900/90 border border-gray-800 text-gray-300 hover:text-emerald-300 hover:border-emerald-500/40 disabled:opacity-40 disabled:cursor-not-allowed transition-colors shadow-xl backdrop-blur-sm"
          data-testid="map-share-view-btn"
          title="Copy a link that opens this exact view"
        >
          {shareCopied
            ? <><Check className="h-3 w-3 text-emerald-400" /> Copied</>
            : <><Share2 className="h-3 w-3" /> Share view</>
          }
        </button>
      </div>

      {/* Expanded Video Overlay */}
      {expandedVideo && (
        <div className="absolute inset-0 z-[2000] flex items-center justify-center bg-black/70 backdrop-blur-sm p-4">
          <div className="relative bg-gray-900 rounded-xl overflow-hidden shadow-2xl max-w-4xl w-full max-h-[90vh] flex flex-col">
            <button onClick={() => setExpandedVideo(null)} className="absolute top-3 right-3 z-10 bg-black/60 hover:bg-black/80 text-white p-2 rounded-full transition-colors" data-testid="close-expanded-video-btn">
              <X className="h-5 w-5" />
            </button>

            <div className="flex flex-col md:flex-row flex-1 overflow-hidden">
              {/* Video Section */}
              <div className="md:w-2/3 flex flex-col">
                <video src={expandedVideo.video_url} className="w-full aspect-video object-contain bg-black" loop autoPlay playsInline webkit-playsinline="true" controls data-testid="expanded-video-player" />
                <div className="p-4 border-t border-gray-800">
                  <h3 className="font-bold text-white text-lg">{expandedVideo.title}</h3>
                  {(() => {
                    const isSold = !!expandedVideo.sold_at && expandedVideo.owner_username && expandedVideo.producer_username && expandedVideo.owner_username !== expandedVideo.producer_username;
                    const showOrig = expandedVideo.show_original_producer !== false;
                    const creditHandle = (isSold && !showOrig) ? expandedVideo.owner_username : (expandedVideo.producer_username || expandedVideo.username);
                    const creditLabel = (isSold && !showOrig) ? 'Owned by' : 'Produced by';
                    return (
                      <p className="text-gray-300 text-sm">
                        <span className="text-gray-500 uppercase tracking-wide text-[10px] mr-1">{creditLabel}</span>
                        <Link to={`/${creditHandle}`} className="text-emerald-400 hover:underline" onClick={() => setExpandedVideo(null)}>
                          @{creditHandle}
                        </Link>
                      </p>
                    );
                  })()}
                  <div className="flex items-center gap-4 mt-2 text-gray-400 text-sm">
                    <span className="flex items-center gap-1"><MapPin className="h-4 w-4" />{expandedVideo.location?.city || "Unknown"}, {expandedVideo.location?.country || ""}</span>
                    <span className="flex items-center gap-1"><ClapIcon className="h-4 w-4 text-pink-500" />{expandedVideo.applaud_count}</span>
                    <span className="flex items-center gap-1"><MessageCircle className="h-4 w-4 text-cyan-400" />{comments.length}</span>
                    <ShareButton video={expandedVideo} compact />
                  </div>
                  {/* Make-offer CTA — only for non-owners. Owners see a
                      "Manage in Dashboard → Offers" hint instead. */}
                  {user && expandedVideo.owner_user_id && expandedVideo.owner_user_id !== user.id && (
                    <button
                      type="button"
                      onClick={() => setOfferingVideo(expandedVideo)}
                      className="mt-3 w-full bg-amber-500 hover:bg-amber-400 text-gray-900 text-sm font-bold py-2 px-3 rounded transition-colors flex items-center justify-center gap-2"
                      data-testid="open-make-offer-btn"
                    >
                      <Sparkles className="h-4 w-4" /> Make an anonymous offer
                    </button>
                  )}
                </div>
              </div>

              {/* Comments Section */}
              <div className="md:w-1/3 border-t md:border-t-0 md:border-l border-gray-800 flex flex-col max-h-[300px] md:max-h-none">
                <div className="p-3 border-b border-gray-800">
                  <h4 className="text-white font-semibold flex items-center gap-2">
                    <MessageCircle className="h-4 w-4 text-cyan-400" />
                    Comments ({comments.length})
                  </h4>
                </div>
                <ScrollArea className="flex-1 p-3">
                  {loadingComments ? (
                    <div className="flex justify-center py-4"><div className="animate-spin h-6 w-6 border-2 border-emerald-500 border-t-transparent rounded-full" /></div>
                  ) : comments.length > 0 ? (
                    <div className="space-y-3">
                      {comments.map((comment) => (
                        <div key={comment.id} className="bg-gray-800/50 rounded-lg p-2">
                          <div className="flex items-center gap-2 mb-1">
                            <Link to={`/${comment.username}`} className="text-emerald-400 text-xs font-medium hover:underline" onClick={() => setExpandedVideo(null)}>
                              @{comment.username}
                            </Link>
                            <span className="text-gray-500 text-[10px]">{new Date(comment.created_at).toLocaleDateString()}</span>
                          </div>
                          <p className="text-gray-300 text-sm">{comment.content || comment.text}</p>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-gray-500 text-sm text-center py-4">No comments yet</p>
                  )}
                </ScrollArea>
                <div className="p-3 border-t border-gray-800">
                  {user ? (
                    <form onSubmit={handleSubmitComment} className="flex gap-2">
                      <Input value={newComment} onChange={(e) => setNewComment(e.target.value)} placeholder="Add a comment..." className="flex-1 bg-gray-800 border-gray-700 text-white text-sm h-9" disabled={submittingComment} />
                      <Button type="submit" size="sm" disabled={!newComment.trim() || submittingComment} className="bg-emerald-600 hover:bg-emerald-500 h-9">
                        <Send className="h-4 w-4" />
                      </Button>
                    </form>
                  ) : (
                    <p className="text-gray-500 text-xs text-center">Sign in to comment</p>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Commission Peek Modal — readable for guests, actionable for users.
          Replaces the old "must be signed in" toast on /map pin taps. */}
      {expandedCommission && (
        <div
          className="absolute inset-0 z-[2000] flex items-center justify-center bg-black/70 backdrop-blur-sm p-4"
          onClick={() => setExpandedCommission(null)}
          data-testid="commission-peek-modal"
        >
          <div
            className="relative bg-gray-900 border border-amber-500/30 rounded-xl shadow-2xl max-w-md w-full p-5"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={() => setExpandedCommission(null)}
              className="absolute top-3 right-3 bg-black/60 hover:bg-black/80 text-white p-1.5 rounded-full transition-colors"
              data-testid="close-commission-peek-btn"
              aria-label="Close"
            >
              <X className="h-4 w-4" />
            </button>

            <div className="flex items-center gap-1.5 mb-2">
              <Sparkles className="h-4 w-4 text-amber-300" />
              <span className="text-amber-300 text-[10px] font-bold uppercase tracking-wider">Open commission</span>
            </div>
            <h3 className="font-semibold text-white text-lg leading-tight mb-1">{expandedCommission.title}</h3>
            {expandedCommission.description && (
              <p className="text-gray-300 text-sm mt-2 leading-relaxed">{expandedCommission.description}</p>
            )}
            <p className="text-gray-500 text-xs mt-3">
              Posted by {expandedCommission.ec_anonymous || !expandedCommission.ec_username
                ? <span className="italic">anonymous EP</span>
                : <span className="text-emerald-400">@{expandedCommission.ec_username}</span>}
              {' · '}{expandedCommission.location?.city || expandedCommission.location?.country || 'Custom point'}
            </p>

            <div className="grid grid-cols-3 gap-2 mt-4">
              <div className="bg-gray-800/70 rounded px-2 py-1.5 text-center">
                <div className="text-[10px] text-gray-500 uppercase">Bounty</div>
                <div className="text-amber-300 font-bold text-sm">+{(expandedCommission.bounty_credits || 0).toFixed(2)} cr</div>
              </div>
              <div className="bg-gray-800/70 rounded px-2 py-1.5 text-center">
                <div className="text-[10px] text-gray-500 uppercase">Max length</div>
                <div className="text-white font-bold text-sm">{expandedCommission.max_duration_seconds}s</div>
              </div>
              <div className="bg-gray-800/70 rounded px-2 py-1.5 text-center">
                <div className="text-[10px] text-gray-500 uppercase">Storage</div>
                <div className="text-white font-bold text-sm">{expandedCommission.storage_days}d</div>
              </div>
            </div>
            <p className="text-gray-500 text-[11px] mt-3">
              Expires {new Date(expandedCommission.expires_at).toLocaleString()}
            </p>

            {user && expandedCommission.ec_user_id !== user.id ? (
              <button
                onClick={() => {
                  const id = expandedCommission.id;
                  setExpandedCommission(null);
                  handleAcceptCommission(id);
                }}
                disabled={accepting === expandedCommission.id}
                className="mt-4 w-full bg-amber-500 hover:bg-amber-400 disabled:opacity-60 text-gray-900 text-sm font-bold py-2.5 px-3 rounded transition-colors"
                data-testid="commission-peek-accept-btn"
              >
                {accepting === expandedCommission.id ? 'Reserving…' : 'Accept this commission'}
              </button>
            ) : !user ? (
              <button
                onClick={() => {
                  setExpandedCommission(null);
                  window.dispatchEvent(new CustomEvent('open-auth'));
                }}
                className="mt-4 w-full bg-amber-500 hover:bg-amber-400 text-gray-900 text-sm font-bold py-2.5 px-3 rounded transition-colors"
                data-testid="commission-peek-signup-btn"
              >
                Sign up free to claim →
              </button>
            ) : (
              <p className="mt-4 text-[11px] text-gray-500 italic text-center">This is your offer.</p>
            )}
          </div>
        </div>
      )}
      {/* Anonymous-offer modal — fires from the expanded-video panel. */}
      {offeringVideo && (
        <MakeOfferModal
          videoId={offeringVideo.id}
          videoTitle={offeringVideo.title}
          onClose={() => setOfferingVideo(null)}
          onSent={() => setOfferingVideo(null)}
        />
      )}
    </div>
  );
};

export default MapPage;
